import React from 'react';
import {
  RiAlignBottom,
  RiAlignCenter,
  RiAlignLeft,
  RiAlignRight,
  RiAlignTop,
  RiAlignVertically,
  RiLayoutColumnLine,
  RiLayoutRowLine,
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
  RiArrowLeftLine,
  RiArrowLeftSLine,
  RiArrowRightLine,
  RiArrowRightUpLine,
  RiBlurOffLine,
  RiBold,
  RiBox3Line,
  RiBringForward,
  RiBringToFront,
  RiBrushLine,
  RiCheckLine,
  RiCheckboxBlankCircleLine,
  RiCheckboxBlankLine,
  RiCheckboxLine,
  RiCircleLine,
  RiCloseLine,
  RiCloudLine,
  RiCodeSSlashLine,
  RiContrastDrop2Line,
  RiContrastLine,
  RiCropLine,
  RiCursorLine,
  RiDeleteBinLine,
  RiDownloadLine,
  RiDragMove2Line,
  RiEqualizerLine,
  RiErrorWarningLine,
  RiEyeLine,
  RiFileCopyLine,
  RiFileLine,
  RiFilter2Line,
  RiFilterLine,
  RiFlipHorizontal2Line,
  RiFlipVertical2Line,
  RiFocus3Line,
  RiFullscreenExitLine,
  RiGroupLine,
  RiImageLine,
  RiLinkUnlink,
  RiItalic,
  RiLandscapeLine,
  RiLetterSpacing2,
  RiLineHeight,
  RiLineHeight2,
  RiLink,
  RiLoader4Line,
  RiLockLine,
  RiLockUnlockLine,
  RiMore2Line,
  RiPaletteLine,
  RiPentagonLine,
  RiPriceTag3Line,
  RiPushpinLine,
  RiQuestionLine,
  RiRadioButtonLine,
  RiRestartLine,
  RiRoundedCorner,
  RiSaveLine,
  RiSearchLine,
  RiSendBackward,
  RiSendToBack,
  RiShadowLine,
  RiShapeLine,
  RiSquareLine,
  RiStackLine,
  RiStarLine,
  RiSubtractLine,
  RiSunLine,
  RiTempHotLine,
  RiText,
  RiToggleLine,
  RiUpload2Line,
} from 'react-icons/ri';

/**
 * Drop-in replacement for legacy element/icons (size + color props, default 14px).
 */
export function createEditorIcon(IconComponent, defaultSize = 14) {
  const EditorIcon = React.forwardRef(function EditorIcon(
    { color = 'currentColor', size = defaultSize, style, ...rest },
    ref,
  ) {
    return (
      <IconComponent
        ref={ref}
        color={color}
        size={size}
        style={{ display: 'block', flexShrink: 0, ...style }}
        aria-hidden={rest['aria-hidden'] ?? true}
        {...rest}
      />
    );
  });
  EditorIcon.displayName =
    IconComponent.displayName || IconComponent.name || 'EditorIcon';
  return EditorIcon;
}

// —— Annotation / selection ——
export const Transparency = createEditorIcon(RiContrastDrop2Line);
export const Shadow = createEditorIcon(RiShadowLine);
export const Stroke = createEditorIcon(RiBrushLine);
export const Position = createEditorIcon(RiDragMove2Line);
export const Duplicate = createEditorIcon(RiFileCopyLine);
export const DeleteOutline = createEditorIcon(RiDeleteBinLine);

export const BringForwardIcon = createEditorIcon(RiBringForward);
export const SendBackwardIcon = createEditorIcon(RiSendBackward);
export const BringToFrontIcon = createEditorIcon(RiBringToFront);
export const SendToBackIcon = createEditorIcon(RiSendToBack);

// —— Text ——
export const FontBold = createEditorIcon(RiBold);
export const FontItalic = createEditorIcon(RiItalic);
export const AlignCenter = createEditorIcon(RiAlignCenter);
export const AlignLeft = createEditorIcon(RiAlignLeft);
export const AlignRight = createEditorIcon(RiAlignRight);
export const AlignTop = createEditorIcon(RiAlignTop);
export const AlignMiddle = createEditorIcon(RiAlignVertically);
export const AlignBottom = createEditorIcon(RiAlignBottom);
export const DistributeHorizontal = createEditorIcon(RiLayoutRowLine);
export const DistributeVertical = createEditorIcon(RiLayoutColumnLine);
export const Spacing = createEditorIcon(RiLetterSpacing2);
export const LineHeight = createEditorIcon(RiLineHeight2);
export const FitTextToContent = createEditorIcon(RiFullscreenExitLine);
export const Text = createEditorIcon(RiText);

// —— Top bar ——
export const Undo = createEditorIcon(RiArrowGoBackLine);
export const Redo = createEditorIcon(RiArrowGoForwardLine);
export const SaveAs = createEditorIcon(RiSaveLine);
export const CrossOutline = createEditorIcon(RiCloseLine);
export const ArrowLeftOutline = createEditorIcon(RiArrowLeftLine);
export const Revert = createEditorIcon(RiRestartLine);
export const Warning = createEditorIcon(RiErrorWarningLine);

// —— Draw tools ——
export const Ellipse = createEditorIcon(RiCircleLine);
export const Square = createEditorIcon(RiSquareLine);
export const Line = createEditorIcon(RiSubtractLine);
export const ArrowTool = createEditorIcon(RiArrowRightUpLine);
export const Polygon = createEditorIcon(RiPentagonLine);
export const PolygonSides = createEditorIcon(RiShapeLine);
export const RadiusCorner = createEditorIcon(RiRoundedCorner);

// —— Adjust / effects ——
export const Brightness = createEditorIcon(RiSunLine);
export const Contrast = createEditorIcon(RiContrastLine);
export const Blur = createEditorIcon(RiBlurOffLine);
export const Saturation = createEditorIcon(RiPaletteLine);
export const Temprature = createEditorIcon(RiTempHotLine);
export const Adjust = createEditorIcon(RiEqualizerLine);

// —— Crop / resize / watermark ——
export const Custom = createEditorIcon(RiCropLine);
export const Landscape = createEditorIcon(RiLandscapeLine);
export const Portrait = createEditorIcon(RiLayoutColumnLine);
export const LockOutline = createEditorIcon(RiLockLine);
export const UnlockOutline = createEditorIcon(RiLockUnlockLine);
export const GroupObjects = createEditorIcon(RiGroupLine);
export const UngroupObjects = createEditorIcon(RiLinkUnlink);
export const Padding = createEditorIcon(RiLayoutColumnLine);
export const UploadOutline = createEditorIcon(RiUpload2Line);

// —— Misc UI ——
export const Arrow = createEditorIcon(RiArrowLeftSLine);
export const ArrowRightAlt = createEditorIcon(RiArrowRightLine);
export const Point = createEditorIcon(RiFocus3Line);
export const LayerOrder = createEditorIcon(RiStackLine);

// —— Remaining index exports (same API, modern glyphs) ——
export const Dot = createEditorIcon(RiCheckboxBlankCircleLine);
export const Tick = createEditorIcon(RiCheckLine);
export const Cross = createEditorIcon(RiCloseLine);
export const Image = createEditorIcon(RiImageLine);
export const File = createEditorIcon(RiFileLine);
export const Select = createEditorIcon(RiCursorLine);
export const Remove = createEditorIcon(RiDeleteBinLine);
export const Download = createEditorIcon(RiDownloadLine);
export const Filter = createEditorIcon(RiFilterLine);
export const Upload = createEditorIcon(RiUpload2Line);
export const Filters = createEditorIcon(RiFilter2Line);
export const Preview = createEditorIcon(RiEyeLine);
export const Search = createEditorIcon(RiSearchLine);
export const More = createEditorIcon(RiMore2Line);
export const Link = createEditorIcon(RiLink);
export const Tags = createEditorIcon(RiPriceTag3Line);
export const QuestionMarkOutline = createEditorIcon(RiQuestionLine);
export const Spinner = createEditorIcon(RiLoader4Line);
export const Circle = createEditorIcon(RiCircleLine);
export const FlipHorizontally = createEditorIcon(RiFlipHorizontal2Line);
export const FlipVertically = createEditorIcon(RiFlipVertical2Line);
export const Target = createEditorIcon(RiFocus3Line);
export const Code = createEditorIcon(RiCodeSSlashLine);
export const ArrowTick = createEditorIcon(RiCheckLine);
export const Box = createEditorIcon(RiBox3Line);
export const PinOutline = createEditorIcon(RiPushpinLine);
export const RadioButton = createEditorIcon(RiRadioButtonLine);
export const RadioButtonUnchecked = createEditorIcon(RiCheckboxBlankLine);
export const Star = createEditorIcon(RiStarLine);
export const CheckBox = createEditorIcon(RiCheckboxLine);
export const CheckBoxUnchecked = createEditorIcon(RiCheckboxBlankLine);
export const SwitcherOff = createEditorIcon(RiToggleLine);
export const SwitcherOn = createEditorIcon(RiToggleLine);
export const CloudWithShadow = createEditorIcon(RiCloudLine);
